import { Link as RouterLink } from 'react-router-dom'
import React, { useState } from 'react'

import {
  Box,
  Button,
  ButtonGroup,
  CloseIcon,
  CommentIcon,
  ExpansionPanelProps,
  Link,
  SearchIcon,
  SidebarExpansionPanel,
  Theme,
  createStyles,
  makeStyles,
  useModal,
} from '@forgeglobal/design-framework'

import { ConversationModal } from 'components/shared/ConversationModal'
import { InteractionFilter, InteractionType, InteractionWithRollupsDataFragment } from 'graphql/bindings/AdminBindings'
import { Routes } from 'util/Routes'
import { SearchInputComponent, SecondaryIconButton } from 'isolated-design-library'
import { formatAbbreviatedMoney } from 'util/NumberUtils'
import { makeConjunction } from 'util/ReactUtils'
import Analytics from 'util/Analytics'

import { InteractionList } from './InteractionList'

export const CommunicationSource: React.FC<{
  interaction: InteractionWithRollupsDataFragment
}> = ({ interaction }) => {
  let childAccounts = interaction.rolled_up_from_child_accounts
  if (childAccounts.length > 0) {
    return (
      <div>
        Conversation with{' '}
        {makeConjunction(
          childAccounts.map(childAccount => (
            <Link key={childAccount.id} component={RouterLink} to={Routes.account(childAccount.id)}>
              {childAccount.name}
            </Link>
          ))
        )}
      </div>
    )
  }

  let parentAccounts = interaction.rolled_up_to_parent_accounts
  if (parentAccounts.length > 0) {
    return (
      <div>
        Conversation rolls up to{' '}
        {makeConjunction(
          parentAccounts.map(parentAccount => (
            <Link key={parentAccount.id} component={RouterLink} to={Routes.account(parentAccount.id)}>
              {parentAccount.name}
            </Link>
          ))
        )}
      </div>
    )
  }

  const source = interaction.source
  if (!source) return null
  switch (source.__typename) {
    case 'AccountSource':
      const account = source.account
      switch (account.type) {
        case 'issuer':
          return (
            <Link component={RouterLink} to={Routes.issuer(account.id)}>
              {`Mentioned in conversation about ${account.name}`}
            </Link>
          )
        default:
          return (
            <Link component={RouterLink} to={Routes.account(account.id)}>
              {`Mentioned in conversation with ${account.name}`}
            </Link>
          )
      }
    case 'OpportunitySource':
      return (
        <Link component={RouterLink} to={Routes.opportunity(source.opportunity.id)}>
          {`Mentioned in conversation about ${
            source.opportunity.company.name
          } ${source.opportunity.trade_intent.type.toUpperCase()} ${formatAbbreviatedMoney(
            source.opportunity.trade_intent.dollar_amount
          )}`}
        </Link>
      )
  }
  return null
}

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    opportunityInteractionsContainer: {
      padding: spacing(2),
    },
    boxClass: {
      display: 'table',
      '& > *': {
        display: 'table-row',
      },
      '& > * > *': {
        display: 'table-cell',
        '&:first-child': {
          whiteSpace: 'nowrap',
        },
        '&:last-child': {
          width: '100%',
        },
      },
    },
  })
)

interface ConversationsSidebarPanelProps extends Omit<ExpansionPanelProps, 'children'> {
  interactions: InteractionWithRollupsDataFragment[]
  currentId: string
  isAccount?: boolean
  accountName?: string
  currentPage: string
  filter: InteractionFilter
  setFilter: (filt: InteractionFilter) => void
  createActionLabel?: string
  resourceName?: string
  defaultContactMethod?: InteractionType
  hideContactMethod?: boolean
  hideRollups?: boolean
  accountId: string
}

export const ConversationsSidebarPanel: React.FC<ConversationsSidebarPanelProps> = ({
  interactions,
  currentId, // this will either be an account id or an opportunity id, depending on context
  accountId, // this should always be an account id, and may be equal to currentId if in an account context
  isAccount,
  accountName,
  filter,
  setFilter,
  currentPage,
  createActionLabel,
  resourceName,
  defaultContactMethod,
  hideContactMethod,
  hideRollups,
  ...rest
}) => {
  const [showModal, hideModal] = useModal(() => {
    return (
      <ConversationModal
        hideModal={hideModal}
        accountName={accountName}
        formType="create"
        objId={currentId}
        accountId={accountId}
        isAccount={isAccount}
        resourceName={resourceName}
        defaultContactMethod={defaultContactMethod}
        hideContactMethod={hideContactMethod}
        hideRollups={hideRollups}
      />
    )
  }, [currentId, isAccount, accountName])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)

  const filteredInteractions = interactions.filter(interaction => {
    if (!searchQuery || !searchOpen) return true
    const notesIncludes = (interaction.notes || '').toLowerCase().includes(searchQuery.toLowerCase())
    const frontIncludes = (interaction?.front_message_group?.content ?? '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
    return notesIncludes || frontIncludes
  })

  const toggleSearch = () => {
    if (searchOpen) {
      setSearchOpen(false)
      setSearchQuery('')
    } else {
      Analytics.track('conversation-search-opened')
      setSearchOpen(true)
    }
  }

  const classes = useStyles()

  const handleChange = (_: React.MouseEvent<HTMLElement>, value: InteractionFilter) => {
    setFilter(value)
  }

  const controlBar = (
    <div style={{ display: 'flex' }}>
      {searchOpen ? (
        <SearchInputComponent
          autoFocus
          placeholder="Search conversations"
          onChange={event => setSearchQuery(event.target.value)}
          value={searchQuery}
        />
      ) : (
        <Button fullWidth onClick={showModal}>
          {createActionLabel || 'Add conversation'}
        </Button>
      )}
      <Box ml={2}>
        <SecondaryIconButton onClick={toggleSearch}>{searchOpen ? <CloseIcon /> : <SearchIcon />}</SecondaryIconButton>
      </Box>
    </div>
  )

  const conversations = (
    <Box mt={2} style={{ opacity: searchOpen && !searchQuery ? 0.4 : 1 }}>
      {currentPage === 'issuer' && (
        <ButtonGroup
          size={'small'}
          exclusive
          onChange={handleChange}
          value={filter}
          options={[
            { label: 'All', value: InteractionFilter.All },
            { label: 'Marketplace/BD', value: InteractionFilter.Sales },
            { label: 'Partnerships', value: InteractionFilter.Partnerships },
          ]}
        />
      )}

      <InteractionList
        interactions={filteredInteractions}
        currentId={currentId}
        isAccount={isAccount}
        accountName={accountName}
        filter={filter}
      />
    </Box>
  )

  return currentPage === 'opportunity' ? (
    <div className={classes.opportunityInteractionsContainer}>
      {controlBar}
      {conversations}
    </div>
  ) : (
    <SidebarExpansionPanel icon={<CommentIcon />} title="Recent Conversations" {...rest}>
      {controlBar}
      {conversations}
    </SidebarExpansionPanel>
  )
}
